import qs from 'qs'

import { serviceConfig } from '@h5-journey/config'
import { useUserStoreOutside } from '@h5-journey/stores'
import { debounce, decryptAES } from '@h5-journey/utils'
import axios, { AxiosRequestConfig } from 'axios'
import { showConfirmDialog, showLoadingToast, showToast, ToastWrapperInstance } from 'vant'
import axiosInstance from './axios'
import { encryptData } from './encryption'

// 加密解密开关
const VITE_USE_CRYPTION = import.meta.env.VITE_USE_CRYPTION

// 统一登录跳转：动态导入 platform，规避 service ↔ platform 循环依赖
// platform.toLogin 已内置防重入（并发 401/多处调用只执行第一次跳转）
const redirectToLogin = (): void => {
  import('@h5-journey/platform')
    .then(({ toLogin }) => toLogin())
    .catch((error) => console.error('加载平台登录模块失败:', error))
}

// 口令封装处理
const handlerToken = (header: Record<string, any>) => {
  const loginToken = useUserStoreOutside().loginToken || ''
  if (!loginToken) return header
  header['loginToken'] = loginToken
  return header
}

// 401退出登录
const noLoginHandle = debounce(() => {
  useUserStoreOutside().clearUserInfo()
  redirectToLogin()
}, 1000)

// 没有token跳转到登录页
export const isHasToken = () => {
  const loginToken = useUserStoreOutside().loginToken || ''
  if (loginToken) return true
  debounce(() => {
    redirectToLogin()
  })()
}

// 处理opt传入参数
const handlerData = (opt: Record<string, any>, apiBase: { prefix?: string } = {}) => {
  const { prefix } = apiBase,
    isMock = opt.mock ?? import.meta.env.VITE_APP_ENV === 'mock' // 是否为mock模式

  if (isMock) {
    opt.url = `/mock${prefix}${opt.url}`
  } else if (!~opt.url.indexOf('http')) {
    // 根据环境变量判断是否使用代理
    const isDev = import.meta.env.VITE_APP_ENV
    opt.baseURL = isDev === 'development' ? '/api' : serviceConfig.domainName
    opt.url = `${prefix}${opt.url}`
    console.log(opt.baseURL, opt.url)
  }

  opt.headers = opt.headers ?? { 'Content-Type': 'application/json' } // 设置默认headers
  opt.headers = handlerToken(opt.headers)
  opt.mock = isMock
  // opt.responseType = opt.responseType ?? (opt.mock ? 'json' : 'text') // 细节需要加括号,上环境情况下后端返回的数据是base64字符串
  opt.responseType = opt.responseType ?? 'json'
  opt.reLogin = opt.reLogin ?? true // 是否判断401状态跳转到登录页面

  return opt
}
// 错误信息
const handlerErrorMessage = (error: boolean | string, message: string, tipsCode: string) => {
  if (
    message?.includes('参数校验错误') ||
    message?.includes('未知系统异常') ||
    tipsCode === 'MOB2230029'
  ) {
    return
  }
  if (tipsCode === 'CTG0000000') {
    message = '系统异常，请联系客服'
  }

  // 特殊订单处理：如果tipsCode为ORDER_CHANGE_PAY_ERROR，则弹出弹窗展示错误message
  if (tipsCode === 'ORDER_CHANGE_PAY_ERROR') {
    const errorMsg = error !== true ? error : (message ?? '网络异常，请稍后重试')
    showConfirmDialog({
      message: errorMsg as string,
      confirmButtonText: '好的',
      showCancelButton: false,
      className: 'pay-error-dialog',
      width: '300px'
    })
    return
  }

  // 如果错误信息包含"非该登录用户"，在app环境下重定向到订单列表页面
  if (message?.includes('非该登录用户') && window.flutter_inappwebview) {
    // 延迟执行重定向，确保用户能看到错误提示
    setTimeout(() => {
      // 在app环境下直接跳转，避免依赖路由初始化
      const target = '/order/list'
      try {
        const url = new URL(target, window.location.origin)
        window.location.replace(url.toString())
      } catch {
        window.location.replace(target)
      }
    }, 3000)
  }

  error &&
    showToast({
      message: error !== true ? error : (message ?? '网络异常，请稍后重试'),
      forbidClick: true
    })
}
// 成功信息
const handlerSuccessMessage = (success: boolean | string, message: string) => {
  success &&
    showToast({
      message: success !== true ? success : (message ?? '成功'),
      type: 'success'
    })
}

enum EResponseDataFields {
  // 返回是否正确的字段名称
  SUCCESS = 'success',
  // 返回数据字段名称
  DATA = 'data',
  // 返回消息字段名称
  MESSAGE = 'message',
  // 返回错误代码名称
  ERROR_CODE = 'errorCode'
}

export interface IRequestConfig {
  prefix?: string
  // 成功码，默认为true
  successCode?: boolean | number
  // 是否需要加密，默认需要
  needEncrypt?: boolean
  // 是否显示成功的toast，默认为false不显示
  showSuccessToast?: boolean
  // 是否显示失败信息的toast，默认为true显示
  showErrorToast?: boolean
  // 传入boolean代表是否显示loading，传入string代表loading的文案
  showLoading?: boolean | string
  // 返回数据的字段名称
  fields?: Partial<Record<EResponseDataFields, string>>
  // 是否需要重新登录，默认为true
  reLogin?: boolean
  // 是否跳过响应处理，默认为false
  // 如果为true则直接返回axios原始返回数据
  skipResponseHandler?: boolean
}

const defaultDataFields: Record<EResponseDataFields, string> = {
  success: 'success',
  data: 'data',
  message: 'message',
  errorCode: 'errorCode'
}

// 业务接口
async function request(
  opt: AxiosRequestConfig,
  {
    prefix = '',
    successCode = true,
    needEncrypt = false, // 默认需要加密，不用设置
    showSuccessToast = false,
    showErrorToast = true,
    showLoading = false,
    fields = defaultDataFields,
    reLogin = true,
    skipResponseHandler = false
  }: IRequestConfig
) {
  opt = handlerData(opt, { prefix }) // 参数预处理
  const finnalFields = { ...defaultDataFields, ...fields }

  // 特殊格式请求处理
  const posts = ['put', 'post', 'patch']
  if (
    posts.includes(opt.method ?? 'get') &&
    opt.headers?.['Content-Type'] === 'application/x-www-form-urlencoded'
  ) {
    opt.data = qs.stringify(opt.data)
  }

  // 最终是否需要加密：needEncrypt 参数为 true 且接口在白名单中
  const finalNeedEncrypt = needEncrypt

  // 加密解密开关
  if (VITE_USE_CRYPTION === 'true' && finalNeedEncrypt) {
    opt.data = encryptData(opt.url ?? '', opt.data)
  }
  // 接口全局加载提示
  let loadingInstance: ToastWrapperInstance | undefined = undefined
  if (showLoading !== false) {
    loadingInstance = showLoadingToast({
      duration: 0,
      forbidClick: true,
      message: showLoading !== true ? showLoading : '加载中……'
    })
  }
  try {
    const result = await axiosInstance(opt) // 请求接口
    loadingInstance && loadingInstance.close()
    const resData = result?.data ?? {}
    // if ((opt.reLogin && result.status === 401) || resData.errorCode === 'MOB2230010' ||  resData.errorCode ==='30010' ) {
    if (
      (reLogin && result.status === 401) ||
      (reLogin && resData.errorCode === 'MOB2230010') ||
      (reLogin && resData.errorCode === '30010')
    ) {
      noLoginHandle()
      return Promise.reject(result)
    }
    // 有设置跳过处理时直接返回axios原始返回数据
    if (skipResponseHandler) {
      return Promise.resolve(result)
    }

    let response = result.data
    // 加密解密开关
    if (VITE_USE_CRYPTION === 'true' && finalNeedEncrypt) {
      // 需要解密
      if (response?.data?.body) {
        // 只解密 body 内容
        response = decryptAES(response.data.body)
        if (!response) {
          return Promise.reject(result)
        }
      }
    }
    const success = result.data[finnalFields.success]
    // 如果 dataField 为空字符串，返回完整的响应对象；否则提取指定字段
    const data = finnalFields.data === '' ? result.data : result.data[finnalFields.data]
    const message = result.data[finnalFields.message]
    const errCode = result.data[finnalFields.errorCode]
    if (success === successCode) {
      // 提前处理正确错误
      handlerSuccessMessage(showSuccessToast, message)
      return Promise.resolve(VITE_USE_CRYPTION === 'true' && finalNeedEncrypt ? response : data)
    } else {
      handlerErrorMessage(showErrorToast, message, errCode)
      // 如果 error: false，返回包含错误信息的对象，让业务代码自己判断；否则 reject
      if (showErrorToast === false) {
        // 如果 dataField 为空字符串，result.data 已经是完整的响应对象，直接返回
        // 否则构造一个包含错误信息的对象
        return Promise.resolve(
          finnalFields.data === ''
            ? result.data
            : {
                success: false,
                message: message || '请求失败'
              }
        )
      }

      return Promise.reject(response)
    }
  } catch (e: any) {
    loadingInstance && loadingInstance.close()
    // 请求被主动取消（如页面切换、上一次请求被新请求覆盖），不弹错误提示
    if (axios.isCancel(e)) {
      return Promise.reject(e)
    }
    const response = e.response
    if (reLogin && response?.status === 401) noLoginHandle()
    else {
      const resData = response?.data ?? {}
      const message = resData[finnalFields.message]
      const errCode = resData[finnalFields.errorCode]
      handlerErrorMessage(showErrorToast, message, errCode)
    }

    return Promise.reject(e)
  }
}

export { request }
